import { FunctionComponent, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { DND_CONTENT, DND_WHATSAPP_URL, DndLanguage } from "./content";
import heroDragonImage from "../../assets/images/dnd/hero-dragon.webp";
import alexandraImage from "../../assets/images/dnd/alexandra.jpg";
import unschoolLogo from "../../assets/images/dnd/unschool-logo.png";
import "./dnd.css";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&family=Noto+Serif+Hebrew:wght@700;800&family=Rubik:wght@400;500;600;700&display=swap";

const DndClub: FunctionComponent = () => {
  const [language, setLanguage] = useState<DndLanguage>("he");
  const content = DND_CONTENT[language];

  const cta = (label: string) => (
    <a
      className="primary-cta"
      href={DND_WHATSAPP_URL}
      target="_blank"
      rel="noreferrer"
      aria-label={content.ctaLabel}
    >
      <span>{label}</span>
      <span className="cta-star" aria-hidden="true">
        ✦
      </span>
    </a>
  );

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: content.lang,
          dir: content.dir,
          className: "dnd-html",
        }}
      >
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:url" content="https://unschool.cool/dnd" />
        <meta
          property="og:image"
          content={`https://unschool.cool${heroDragonImage}`}
        />
        <link rel="preload" as="image" href={heroDragonImage} />
        <link rel="stylesheet" href={FONTS_URL} />
      </Helmet>
      <main className="dnd-club" lang={content.lang} dir={content.dir}>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-art" aria-hidden="true" />
          <div className="hero-shade" aria-hidden="true" />
          <header className="site-header">
            <Link className="brand" to="/" aria-label="Unschool">
              <img className="brand-logo" src={unschoolLogo} alt="Unschool" />
            </Link>
            <nav aria-label={content.navLabel}>
              {content.nav.map((item, index) => (
                <a key={item} href={`#section-${index + 1}`}>
                  {item}
                </a>
              ))}
            </nav>
            <div className="language-switch" aria-label={content.languageLabel}>
              <button
                className={language === "he" ? "active" : ""}
                type="button"
                onClick={() => setLanguage("he")}
                aria-pressed={language === "he"}
              >
                HE
              </button>
              <span aria-hidden="true">|</span>
              <button
                className={language === "en" ? "active" : ""}
                type="button"
                onClick={() => setLanguage("en")}
                aria-pressed={language === "en"}
              >
                EN
              </button>
            </div>
          </header>
          <div className="hero-inner" id="top">
            <div className="hero-copy">
              <p className="eyebrow">{content.eyebrow}</p>
              <h1 id="hero-title">
                {content.title}
                <span>{content.titleAccent}</span>
              </h1>
              <p className="hero-intro">{content.intro}</p>
              {cta(content.cta)}
              <dl className="fact-grid">
                {content.facts.map(([term, description]) => (
                  <div className="fact" key={`${term}-${description}`}>
                    <dt>{term}</dt>
                    <dd>{description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="about section-shell" id="section-1">
          <div className="section-heading">
            <p className="section-kicker">{content.aboutKicker}</p>
            <h2>{content.aboutTitle}</h2>
          </div>
          <div className="about-grid">
            <p className="about-lead">{content.aboutBody}</p>
            <aside className="parchment-note">
              <span className="note-symbol" aria-hidden="true">
                ✦
              </span>
              <p>{content.aboutNote}</p>
            </aside>
          </div>
          <div className="benefits">
            <h3>{content.benefitsTitle}</h3>
            <div className="benefit-grid">
              {content.benefits.map(([title, description], index) => (
                <article
                  className={`benefit-card benefit-card-${index + 1}`}
                  key={title}
                >
                  <span className="benefit-seed" aria-hidden="true" />
                  <h4>{title}</h4>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="journey" id="section-2">
          <div className="journey-inner section-shell">
            <div className="section-heading compact">
              <p className="section-kicker">{content.journeyKicker}</p>
              <h2>{content.journeyTitle}</h2>
            </div>
            <div className="journey-list">
              {content.journey.map(([step, title, description], index) => (
                <article
                  className={`journey-step journey-step-${index + 1}`}
                  key={step}
                >
                  <span className="step-number">{step}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="host section-shell" id="section-3">
          <div className="host-card">
            <figure className="host-portrait">
              <div className="host-photo-window">
                <img src={alexandraImage} alt={content.hostPortraitAlt} />
              </div>
            </figure>
            <div className="host-stats">
              {content.hostStats.map(([value, label]) => (
                <div key={label}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>
            <div className="host-copy">
              <p className="section-kicker">{content.hostKicker}</p>
              <h2>{content.hostTitle}</h2>
              <p className="host-lead">{content.hostBody}</p>
              <p className="host-note">{content.hostNote}</p>
            </div>
          </div>
        </section>

        <section className="details section-shell" id="section-4">
          <div className="section-heading compact">
            <p className="section-kicker">{content.detailsKicker}</p>
            <h2>{content.detailsTitle}</h2>
          </div>
          <dl className="details-grid">
            {content.details.map(([term, description]) => (
              <div className="detail-card" key={term}>
                <dt>{term}</dt>
                <dd>{description}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="final-cta">
          <div className="final-inner">
            <p className="section-kicker">{content.finalKicker}</p>
            <h2>{content.finalTitle}</h2>
            <p>{content.finalBody}</p>
            {cta(content.finalCta)}
          </div>
        </section>

        <footer>
          <p>{content.footer}</p>
          <div className="footer-languages" aria-label={content.languageLabel}>
            <button type="button" onClick={() => setLanguage("he")}>
              עברית
            </button>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => setLanguage("en")}>
              English
            </button>
          </div>
        </footer>
      </main>
    </>
  );
};

export default DndClub;
